import { useAppTranslation } from "@/utils";
import { Button, Flex, Typography, message, notification, theme } from "@payping/web-components";
import { FC, useEffect, useRef, useState } from "react";
import SignaturePad from "signature_pad";

interface DrawProps {
  onFinish: (file: File) => void;
  loading: boolean;
}
const UserSignContractCreateSign: FC<DrawProps> = ({ onFinish, loading }) => {
  const { t } = useAppTranslation();

  const [canvasRef, setCanvasRef] = useState<HTMLCanvasElement | null>(null);
  const signaturePadRef = useRef<SignaturePad>(null);

  const { token } = theme.useToken();
  const [notify, contextHolder] = notification.useNotification({
    top: 16,
    duration: 5,
    maxCount: 1,
    placement: "top",
  });

  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    const signaturePad = signaturePadRef.current;
    const canvas = canvasRef;
    if (canvas) {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      // eslint-disable-next-line react-hooks/immutability
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext("2d")?.scale(ratio, ratio);
      (signaturePadRef as any).current = new SignaturePad(canvas, { backgroundColor: "#ffffff", penColor: "#000000" });
    }
    return () => {
      if (signaturePad) {
        signaturePad.clear();
        signaturePad.off();
      }
    };
  }, [canvasRef]);

  const clearDraw = () => {
    const signaturePad = signaturePadRef.current;
    if (signaturePad) {
      signaturePad.clear();
    }
  };

  async function confirmDraw() {
    const maxSize = 50 * 1024;
    const signaturePad = signaturePadRef.current;

    if (signaturePad) {
      if (signaturePad.isEmpty()) {
        notify.error({
          message: String(t("shop.bnpl.activation.formDrawer.steps.signature.draw.errorForDraw")),
          closable: true,
        });
      }
      // continue if draw
      else {
        const data = signaturePad.toDataURL("image/jpeg");
        const blob = await (await fetch(data)).blob();

        if (maxSize && blob.size >= maxSize) {
          message.error({ content: t("shop.bnpl.activation.formDrawer.steps.signature.draw.sizeError") });
        } else {
          await onFinish(new File([blob], "signature.jpeg", { type: "image/jpeg" }));
        }
      }
    }
  }

  return (
    <Flex vertical className="tw:w-full" gap={"large"}>
      <Typography.Title level={5} className="tw:m-0! tw:text-center">
        {t("overview.userSignContract.modal.steps.create-sign.title")}
      </Typography.Title>
      <Typography.Paragraph className="tw:m-0! tw:text-center">
        {t("overview.userSignContract.modal.steps.create-sign.description")}
      </Typography.Paragraph>
      {contextHolder}
      <Flex vertical gap={"small"}>
        <canvas
          className="tw:h-[40dvh] tw:w-full tw:rounded-lg"
          style={{ border: "1px solid", borderColor: token.colorBorder }}
          ref={(ref) => setCanvasRef(ref)}
        ></canvas>
        <Button
          onClick={() => {
            clearDraw();
          }}
        >
          {t("overview.userSignContract.modal.steps.create-sign.redraw")}
        </Button>
      </Flex>
      <Flex gap={"middle"}>
        <Button variant="solid" color="primary" disabled={loading} onClick={confirmDraw}>
          {t("overview.userSignContract.modal.steps.create-sign.submit")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default UserSignContractCreateSign;
